import { supabase } from '../lib/supabase';

/**
 * System Configuration Service
 * Manages system-wide configuration settings stored in the database
 */

/**
 * Get a configuration value by key
 * @param {string} key - Configuration key
 * @returns {Promise<string|null>} Configuration value
 */
export async function getConfigValue(key) {
  try {
    const { data, error } = await supabase
      ?.from('system_configuration')
      ?.select('config_value')
      ?.eq('config_key', key)
      ?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        // No rows returned - key doesn't exist
        return null;
      }
      throw error;
    }

    return data?.config_value || null;
  } catch (error) {
    console.error(`Error fetching config value for ${key}:`, error);
    return null;
  }
}

/**
 * Get multiple configuration values
 * @param {string[]} keys - Array of configuration keys
 * @returns {Promise<Object>} Object with key-value pairs
 */
export async function getConfigValues(keys) {
  try {
    const { data, error } = await supabase
      ?.from('system_configuration')
      ?.select('config_key, config_value, config_type')
      ?.in('config_key', keys);

    if (error) throw error;

    // Convert array to object and parse values based on type
    const config = {};
    data?.forEach(item => {
      const value = item?.config_value;
      
      // Parse value based on type
      if (item?.config_type === 'boolean') {
        config[item?.config_key] = value === 'true';
      } else if (item?.config_type === 'number') {
        config[item?.config_key] = value ? parseFloat(value) : null;
      } else {
        config[item?.config_key] = value;
      }
    });

    return config;
  } catch (error) {
    console.error('Error fetching config values:', error);
    return {};
  }
}

/**
 * Get all configuration values
 * @returns {Promise<Object>} Object with all configuration key-value pairs
 */
export async function getAllConfig() {
  try {
    const { data, error } = await supabase
      ?.from('system_configuration')
      ?.select('config_key, config_value, config_type, description');

    if (error) throw error;

    // Convert array to object and parse values based on type
    const config = {};
    data?.forEach(item => {
      const value = item?.config_value;
      
      // Parse value based on type
      if (item?.config_type === 'boolean') {
        config[item?.config_key] = value === 'true';
      } else if (item?.config_type === 'number') {
        config[item?.config_key] = value ? parseFloat(value) : null;
      } else {
        config[item?.config_key] = value;
      }
    });

    return config;
  } catch (error) {
    console.error('Error fetching all config:', error);
    return {};
  }
}

/**
 * Set a configuration value
 * @param {string} key - Configuration key
 * @param {string|number|boolean} value - Configuration value
 * @returns {Promise<boolean>} Success status
 */
export async function setConfigValue(key, value) {
  try {
    // Convert value to string for storage
    const stringValue = value?.toString();

    const { error } = await supabase
      ?.from('system_configuration')
      ?.update({ 
        config_value: stringValue,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('config_key', key);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error(`Error setting config value for ${key}:`, error);
    throw new Error(`فشل في حفظ الإعداد: ${error?.message}`);
  }
}

/**
 * Set multiple configuration values
 * @param {Object} configObject - Object with key-value pairs to update
 * @returns {Promise<boolean>} Success status
 */
export async function setConfigValues(configObject) {
  try {
    const updates = Object.entries(configObject)?.map(([key, value]) => ({
      config_key: key,
      config_value: value?.toString(),
      updated_at: new Date()?.toISOString()
    }));

    // Update each config value
    const promises = updates?.map(update =>
      supabase
        ?.from('system_configuration')
        ?.update({ 
          config_value: update?.config_value,
          updated_at: update?.updated_at
        })
        ?.eq('config_key', update?.config_key)
    );

    const results = await Promise.all(promises);
    
    // Check if any updates failed
    const hasError = results?.some(result => result?.error);
    if (hasError) {
      throw new Error('فشل في حفظ بعض الإعدادات');
    }

    return true;
  } catch (error) {
    console.error('Error setting config values:', error);
    throw new Error(`فشل في حفظ الإعدادات: ${error?.message}`);
  }
}

/**
 * Test Google Maps API connection
 * @param {string} apiKey - Google Maps API key to test
 * @returns {Promise<Object>} Test result with status and message
 */
export async function testGoogleMapsConnection(apiKey) {
  try {
    if (!apiKey || apiKey?.trim() === '') {
      return {
        success: false,
        message: 'مفتاح API غير متوفر'
      };
    }

    // Test with Geocoding API using CORS-friendly approach
    const testAddress = 'Amman, Jordan';
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(testAddress)}&key=${apiKey}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      // Check if response is ok
      if (!response?.ok) {
        return {
          success: false,
          message: `فشل الاتصال: HTTP ${response?.status}`
        };
      }

      const data = await response?.json();

      if (data?.status === 'OK' && data?.results?.length > 0) {
        return {
          success: true,
          message: 'تم الاتصال بنجاح! مفتاح API صالح ويعمل بشكل صحيح.'
        };
      } else if (data?.status === 'REQUEST_DENIED') {
        return {
          success: false,
          message: `تم رفض الطلب. ${data?.error_message || 'تحقق من صحة مفتاح API والصلاحيات. تأكد من تفعيل Geocoding API في Google Cloud Console.'}`
        };
      } else if (data?.status === 'OVER_QUERY_LIMIT') {
        return {
          success: false,
          message: 'تم تجاوز حد الاستعلامات اليومي. تحقق من حصة API الخاصة بك.'
        };
      } else if (data?.status === 'INVALID_REQUEST') {
        return {
          success: false,
          message: 'طلب غير صالح. تحقق من تنسيق مفتاح API.'
        };
      } else if (data?.status === 'ZERO_RESULTS') {
        // This means API key works but no results found (still valid)
        return {
          success: true,
          message: 'مفتاح API صالح ويعمل بشكل صحيح.'
        };
      } else {
        return {
          success: false,
          message: `فشل الاتصال: ${data?.status}${data?.error_message ? ' - ' + data?.error_message : ''}`
        };
      }
    } catch (fetchError) {
      // Handle network or CORS errors
      if (fetchError?.message?.includes('Failed to fetch') || fetchError?.message?.includes('NetworkError')) {
        return {
          success: false,
          message: 'خطأ في الاتصال بالشبكة. تحقق من اتصال الإنترنت أو قيود CORS على مفتاح API.'
        };
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error testing Google Maps connection:', error);
    return {
      success: false,
      message: `خطأ في الاتصال: ${error?.message || 'حدث خطأ غير متوقع'}`
    };
  }
}

export default {
  getConfigValue,
  getConfigValues,
  getAllConfig,
  setConfigValue,
  setConfigValues,
  testGoogleMapsConnection
};